import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface WizardStep {
  id: string;
  title: string;
  render: (ctx: WizardContext) => ReactNode;
  optional?: boolean;
  validate?: (data: Record<string, unknown>) => boolean | string;
}

export interface WizardContext {
  data: Record<string, unknown>;
  setData: (patch: Record<string, unknown>) => void;
  next: () => void;
  prev: () => void;
  go: (id: string) => void;
}

export interface WizardProps {
  steps: WizardStep[];
  initialData?: Record<string, unknown>;
  onFinish: (data: Record<string, unknown>) => void;
  onCancel?: () => void;
  lang?: 'he' | 'en';
}

export function Wizard({ steps, initialData = {}, onFinish, onCancel, lang = 'he' }: WizardProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [data, setData] = useState<Record<string, unknown>>(initialData);
  const [error, setError] = useState<string | null>(null);
  const stepperRef = useRef<HTMLDivElement>(null);

  // הסטפר גולש אופקית כשיש הרבה צעדים — מגלגלים כדי שהצעד הפעיל
  // (והבא אחריו) תמיד ייראה, אחרת צעדים "צצים משום מקום".
  useEffect(() => {
    const el = stepperRef.current?.querySelector<HTMLElement>('[data-step-active="true"]');
    if (typeof el?.scrollIntoView === 'function') {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [stepIndex]);

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  function patchData(patch: Record<string, unknown>) {
    setData((prev) => ({ ...prev, ...patch }));
  }

  function next() {
    if (step.validate) {
      const result = step.validate(data);
      if (result !== true) {
        setError(typeof result === 'string' ? result : (lang === 'he' ? 'יש להשלים את הנדרש' : 'Required'));
        return;
      }
    }
    setError(null);
    if (isLast) {
      onFinish(data);
    } else {
      setStepIndex((i) => i + 1);
    }
  }

  function prev() {
    setError(null);
    setStepIndex((i) => Math.max(0, i - 1));
  }

  function go(id: string) {
    const idx = steps.findIndex((s) => s.id === id);
    if (idx >= 0) setStepIndex(idx);
  }

  const ctx: WizardContext = { data, setData: patchData, next, prev, go };

  return (
    <div className="stack">
      {/* Stepper */}
      <div ref={stepperRef} style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
        {steps.map((s, i) => {
          const active = i === stepIndex;
          const done = i < stepIndex;
          return (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                data-step-active={active || undefined}
                onClick={() => i <= stepIndex && setStepIndex(i)}
                disabled={i > stepIndex}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: active ? 'var(--accent-ink)' : done ? 'var(--ok)' : 'var(--muted)',
                  cursor: i <= stepIndex ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                  fontSize: 'var(--t-sm)',
                  fontWeight: active ? 600 : 400,
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: active ? 'var(--accent)' : done ? 'var(--ok)' : 'var(--surface-2)',
                    color: active || done ? 'var(--on-accent)' : 'var(--text-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {done ? '✓' : i + 1}
                </span>
                {s.title}
              </button>
              {i < steps.length - 1 && (
                <span style={{ width: 16, height: 1, background: 'var(--line)' }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Active step */}
      <div style={{ padding: 'var(--pad-card)', background: 'var(--surface)', borderRadius: 'var(--r-3)', border: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <h3 style={{ margin: '0 0 var(--gap) 0', fontSize: 'var(--t-xl)' }}>{step.title}</h3>
          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', flexShrink: 0 }}>
            {lang === 'he' ? `שלב ${stepIndex + 1} מתוך ${steps.length}` : `Step ${stepIndex + 1} of ${steps.length}`}
          </span>
        </div>
        {step.render(ctx)}
      </div>

      {error && (
        <div
          role="alert"
          style={{ padding: 12, background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)' }}
        >
          {error}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button type="button" className="btn ghost" onClick={onCancel}>
          {lang === 'he' ? 'ביטול' : 'Cancel'}
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {stepIndex > 0 && (
            <button type="button" className="btn" onClick={prev}>
              {lang === 'he' ? 'הקודם' : 'Back'}
            </button>
          )}
          <button type="button" className="btn primary" onClick={next}>
            {isLast ? (lang === 'he' ? 'סיים' : 'Finish') : (lang === 'he' ? 'המשך' : 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
}
